/**
 * Login Page
 * V3 dark-themed login interface
 */

import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { LanguageSwitcher } from '../components/layout/LanguageSwitcher.jsx';
import { getSubdomainFromHost } from '../utils/tenant';
import { API_BASE_URL } from '../../api';
import { APP_VERSION, APP_COPYRIGHT_YEAR } from '../../version';

export default function LoginPage() {
  const { t } = useTranslation();
  const { login, isLoading, error: authError } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [tenantInfo, setTenantInfo] = useState(null);
  const [loadingTenant, setLoadingTenant] = useState(true);

  // Memoize subdomain to prevent re-fetching tenant on every render
  const [subdomain] = useState(() => getSubdomainFromHost());

  // Fetch tenant info on mount
  // NOTE: Disabled temporarily - /auth/tenant endpoint returns 404 and causes infinite loop
  // TODO: Either implement the endpoint or remove tenant info display from login page
  useEffect(() => {
    // Skip tenant fetch for now to avoid 404 errors
    setLoadingTenant(false);

    /* DISABLED - 404 endpoint causing infinite loop
    async function fetchTenant() {
      try {
        const headers = {};
        if (subdomain) {
          headers['X-Tenant-Subdomain'] = subdomain;
        }

        const response = await fetch(`${API_BASE_URL}/auth/tenant`, { headers });
        const data = await response.json();

        if (data.ok) {
          setTenantInfo(data.data);
        }
      } catch (err) {
        console.error('Failed to fetch tenant:', err);
      } finally {
        setLoadingTenant(false);
      }
    }

    fetchTenant();
    */
  }, [subdomain]);

  // Handle form submission
  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError(t('auth.login.missingCredentials'));
      return;
    }

    const result = await login(email, password);

    if (!result.success) {
      setError(result.error || t('auth.login.failed'));
    }
  };

  // Keyboard shortcut for submit
  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
      </div>

      {/* Login card */}
      <div className="relative w-full max-w-md">
        <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 shadow-2xl">
          {/* Language */}
          <div className="flex justify-end -mt-3 -mr-3 mb-1">
            <LanguageSwitcher variant="pills" />
          </div>

          {/* Logo and title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 mb-4">
              <img src="/favicon.svg" alt="FleetManager" className="w-20 h-20" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{t('common.app.name')}</h1>

            {/* Tenant name */}
            {loadingTenant ? (
              <div className="h-6 w-32 mx-auto bg-slate-700/50 rounded animate-pulse" />
            ) : tenantInfo ? (
              <p className="text-slate-400">{tenantInfo.name}</p>
            ) : (
              <p className="text-slate-400">{t('auth.login.subtitle')}</p>
            )}
          </div>

          {/* Error message */}
          {(error || authError) && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error || authError}</p>
            </div>
          )}

          {/* Login form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                {t('auth.login.emailLabel')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('auth.login.emailPlaceholder')}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700/50 rounded-lg
                         text-white placeholder-slate-500
                         focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50
                         transition-all duration-200"
                autoComplete="email"
                autoFocus
              />
            </div>

            {/* Password field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2">
                {t('auth.login.passwordLabel')}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 pr-12 bg-slate-900/50 border border-slate-700/50 rounded-lg
                           text-white placeholder-slate-500
                           focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50
                           transition-all duration-200"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1
                           text-slate-400 hover:text-white transition-colors"
                  aria-label={showPassword ? t('auth.login.hidePassword') : t('auth.login.showPassword')}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Remember me and forgot password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-700 bg-slate-900/50
                           text-blue-500 focus:ring-blue-500/50 focus:ring-offset-0"
                />
                <span className="text-sm text-slate-400">{t('auth.login.rememberMe')}</span>
              </label>

              <button
                type="button"
                className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                {t('auth.login.forgotPassword')}
              </button>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-500
                       text-white font-medium rounded-lg
                       focus:outline-none focus:ring-2 focus:ring-blue-500/50
                       transition-all duration-200
                       disabled:opacity-50 disabled:cursor-not-allowed
                       flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('auth.login.submitting')}
                </>
              ) : (
                t('auth.login.submit')
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-700/50">
            <p className="text-center text-sm text-slate-500">
              {t('auth.login.needHelp')}{' '}
              <button className="text-blue-400 hover:text-blue-300 transition-colors">
                {t('auth.login.contactSupport')}
              </button>
            </p>
          </div>
        </div>

        {/* Version info */}
        <p className="text-center text-xs text-slate-600 mt-4">{t('auth.login.version', { version: APP_VERSION, year: APP_COPYRIGHT_YEAR })}</p>
      </div>
    </div>
  );
}
